import React from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useAuth } from '../contexts/AuthContext';

/**
 * Home page component for the BagABoard application.
 * 
 * This component serves as the landing page, showcasing the service
 * features, benefits, and call-to-action buttons for users.
 * 
 * @author BagABoard Team
 */
function Home() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="home-page">
      {/* Under Construction Notice */}
      <Alert variant="warning" className="mb-0 text-center rounded-0 border-0">
        <Container>
          <div className="d-flex align-items-center justify-content-center gap-2">
            <i className="fas fa-tools"></i>
            <strong>Site Under Construction:</strong>
            <span>We're currently improving our services. Some features may be temporarily unavailable.</span>
          </div>
        </Container>
      </Alert>

      {/* Hero Section */}
      <section className="hero-section text-center text-white">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">
                Ship Your Bags Worldwide with Confidence
              </h1>
              <p className="lead mb-4">
                Skip the hassle of carrying heavy luggage. Send your bags ahead 
                and travel light with BagAboard's reliable shipping service.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                {isAuthenticated() ? (
                  <LinkContainer to="/route-prices">
                    <Button variant="light" size="lg" className="px-4">
                      <i className="fas fa-shipping-fast me-2"></i>
                      Book Now
                    </Button>
                  </LinkContainer>
                ) : (
                  <LinkContainer to="/route-prices">
                    <Button variant="light" size="lg" className="px-4">
                      <i className="fas fa-user-plus me-2"></i>
                      Get Started
                    </Button>
                  </LinkContainer>
                )}
                <LinkContainer to="/track">
                  <Button variant="outline-light" size="lg" className="px-4">
                    <i className="fas fa-search me-2"></i>
                    Track Package
                  </Button>
                </LinkContainer>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-4">
            <Col>
              <h2 className="display-6 fw-bold mb-3">EASY LUGGAGE SHIPPING WITH BagAboard</h2>
              <p className="lead text-muted">
                Our door to door shipping service helps you travel the world and relocate between countries without being weighed down
              </p>
            </Col>
          </Row>

          <Row className="g-4 mt-4">
            {/* Step 1 - Book a Collection */}
            <Col md={6} lg={3}>
              <Card className="border-0 h-100 bg-white shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3" style={{fontSize: '3rem', color: 'var(--primary-color)'}}>
                    <i className="fas fa-laptop"></i>
                  </div>
                  <h5 className="fw-bold mb-3">BOOK A COLLECTION</h5>
                  <p className="text-muted small">
                    Book online using our simple booking system for your preferred collection date
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Step 2 - Attach Your Labels */}
            <Col md={6} lg={3}>
              <Card className="border-0 h-100 bg-white shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3" style={{fontSize: '3rem', color: 'var(--primary-color)'}}>
                    <i className="fas fa-box-open"></i>
                  </div>
                  <h5 className="fw-bold mb-3">ATTACH YOUR LABELS</h5>
                  <p className="text-muted small">
                    You can print your labels at home or we can send them to you
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Step 3 - Collection Day */}
            <Col md={6} lg={3}>
              <Card className="border-0 h-100 bg-white shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3" style={{fontSize: '3rem', color: 'var(--primary-color)'}}>
                    <i className="fas fa-dolly"></i>
                  </div>
                  <h5 className="fw-bold mb-3">COLLECTION DAY</h5>
                  <p className="text-muted small">
                    We'll collect your items from your home, office, university or hotel
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Step 4 - Delivery */}
            <Col md={6} lg={3}>
              <Card className="border-0 h-100 bg-white shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3" style={{fontSize: '3rem', color: 'var(--primary-color)'}}>
                    <i className="fas fa-shipping-fast"></i>
                  </div>
                  <h5 className="fw-bold mb-3">DELIVERY</h5>
                  <p className="text-muted small">
                    Track your items from pick-up to delivery at your chosen destination
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Why Choose BagAboard?</h2>
              <p className="lead text-muted">
                We make luggage shipping simple, safe, and affordable
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {/* Feature 1 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-shield-alt text-primary"></i>
                  </div>
                  <h3>Secure & Insured</h3>
                  <p className="text-muted">
                    Your belongings are protected with comprehensive insurance 
                    coverage and secure handling throughout the journey.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Feature 2 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-globe text-primary"></i>
                  </div>
                  <h3>Worldwide Delivery</h3>
                  <p className="text-muted">
                    Ship to over 200 countries and territories worldwide with 
                    reliable delivery times and competitive rates.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Feature 3 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-clock text-primary"></i>
                  </div>
                  <h3>Fast & Reliable</h3>
                  <p className="text-muted">
                    Quick pickup and delivery with real-time tracking so you 
                    always know where your bags are.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Feature 4 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-dollar-sign text-primary"></i>
                  </div>
                  <h3>Affordable Rates</h3>
                  <p className="text-muted">
                    Competitive pricing with no hidden fees. Pay only for what 
                    you ship with transparent cost calculation.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Feature 5 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-headset text-primary"></i>
                  </div>
                  <h3>24/7 Support</h3>
                  <p className="text-muted">
                    Round-the-clock customer support to help you with any 
                    questions or concerns about your shipment.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            {/* Feature 6 */}
            <Col md={4}>
              <Card className="feature-card border-0 h-100">
                <Card.Body className="text-center">
                  <div className="icon mb-3">
                    <i className="fas fa-mobile-alt text-primary"></i>
                  </div>
                  <h3>Easy Booking</h3>
                  <p className="text-muted">
                    Simple online booking process with instant quotes and 
                    flexible pickup scheduling that fits your travel plans.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h2 className="display-5 fw-bold mb-3">Ready to Ship Your Bags?</h2>
              <p className="lead text-muted mb-4">
                Join thousands of satisfied customers who trust BagAboard 
                for their luggage shipping needs.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                {isAuthenticated() ? (
                  <LinkContainer to="/route-prices">
                    <Button variant="primary" size="lg" className="px-4">
                      <i className="fas fa-shipping-fast me-2"></i>
                      Start Shipping
                    </Button>
                  </LinkContainer>
                ) : (
                  <>
                    <LinkContainer to="/register">
                      <Button variant="primary" size="lg" className="px-4">
                        <i className="fas fa-user-plus me-2"></i>
                        Sign Up Free
                      </Button>
                    </LinkContainer>
                    <LinkContainer to="/about">
                      <Button variant="outline-primary" size="lg" className="px-4">
                        <i className="fas fa-info-circle me-2"></i>
                        Learn More
                      </Button>
                    </LinkContainer>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

    </div>
  );
}

export default Home;
